import { useCampaignViewPage } from './CampaignView.hooks';
import { DesignTemplateSummary } from '@app/components';
import { MessageTemplateChannel } from '@app/utils/consts';
import ArrowBackIcon from '@app/assets/icons/back.svg?react';
import { useMemo } from 'react';
import eyeIcon from '@app/assets/icons/eye.png';
import dayjs from 'dayjs';
import EmailIcon from '@app/assets/icons/mail-box.svg?react';
import CalendarCheckIcon from '@app/assets/icons/calendar-check.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import RemoveIcon from '@app/assets/icons/remove-circle.svg?react';

export const CampaignView = () => {
  const { navigate, campaignData, isLoading } = useCampaignViewPage();

  const campaign = campaignData?.data;
  const customers = useMemo(() => campaign?.customers || [], [campaign]);
  const business = campaign?.business;
  const schedules = campaign?.schedules || [];

  // Parse platforms as array (comma-separated string)
  const platforms = (campaign?.platforms || '')
    .split(',')
    .map((p: string) => p.trim().toLowerCase());
  const hasEmail = platforms.includes('email');
  const hasSMS = platforms.includes('sms');

  if (isLoading) {
    return <div className="container mx-auto p-4">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-base-ghostWhite">
      {/* Header */}
      <div className="bg-white border-b border-b-base-ghostWhite ">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h2 className="font-bold text-[28px] flex items-center">
            <button onClick={() => navigate('/campaigns')} className="mr-2">
              <ArrowBackIcon />
            </button>
            {campaign?.name || 'Campaign name'}
            <span className="ml-4 px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-semibold">
              {campaign?.status === 'ACTIVE' ? 'Active' : campaign?.status}
            </span>
          </h2>
        </div>
      </div>
      {/* Main Content */}
      <div className="container mx-auto p-2 md:p-4">
        <div className="grid grid-cols-1 md:grid-cols-8 gap-4 md:p-4">
          {/* Left Column */}
          <div className="col-span-1 md:col-span-3 space-y-4">
            {/* Campaign Information Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Campaign Information</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[320px]">
                  <tbody>
                    <tr>
                      <td className="w-[130px] text-xs text-base-coolGray">
                        CAMPAIGN ID
                      </td>
                      <td>{campaign?.id || '-'}</td>
                    </tr>
                    <tr>
                      <td className="w-[130px] text-xs text-base-coolGray">
                        CAMPAIGN NAME
                      </td>
                      <td>{campaign?.name || '-'}</td>
                    </tr>
                    <tr>
                      <td className="w-[130px] text-xs text-base-coolGray">
                        CREATED
                      </td>
                      <td>
                        {campaign?.createdAt
                          ? new Date(campaign.createdAt).toLocaleString()
                          : '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[130px] text-xs text-base-coolGray">
                        CAMPAIGN DESCRIPTION
                      </td>
                      <td>{campaign?.description || '-'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Send campaign via Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Send campaign via</h5>
              <div className="space-y-3">
                {hasEmail && <DesignTemplateSummary channel={MessageTemplateChannel.EMAIL} linkedTemplate={campaign?.emailTemplate} />}
                {hasSMS && <DesignTemplateSummary channel={MessageTemplateChannel.SMS} linkedTemplate={campaign?.smsTemplate} />}
              </div>
            </div>

            {/* Sender Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Sender</h5>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="text-xs text-base-coolGray">
                      BUSINESS NAME
                    </div>
                    <div className="text-xs">{business?.name || '-'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="text-xs text-base-coolGray">
                      BUSINESS ADDRESS
                    </div>
                    <div className="text-xs">{business?.address || '-'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded flex items-center justify-center">
                    <EmailIcon />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-base-coolGray">
                      BUSINESS EMAIL
                    </div>
                    <div className="text-xs">
                      {campaign?.senderEmail || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Follow-ups Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Follow-ups</h5>
              <div className="space-y-4">
                {schedules.length > 0 ? (
                  schedules.map((schedule: any, idx: number) => {
                    const scheduleDate = dayjs(schedule.start);
                    const isPast = scheduleDate.isBefore(dayjs());
                    const isFirstSchedule = idx === 0;

                    return (
                      <div key={schedule.id} className="space-y-2">
                        {isFirstSchedule && isPast && (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded flex items-center justify-center">
                              <CalendarCheckIcon />
                            </div>
                            <div className="text-xs text-base-coolGray">
                              SENT ON
                            </div>
                            <div className="bg-white border rounded px-3 py-1 text-xs">
                              {scheduleDate.format('MMM DD, YYYY')}
                            </div>
                            <div className="flex gap-1">
                              <button className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px]">
                                <img
                                  src={eyeIcon}
                                  alt="eye"
                                  className="w-4 h-4 mr-2"
                                />
                                Email
                              </button>
                              <button className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px]">
                                <img
                                  src={eyeIcon}
                                  alt="eye"
                                  className="w-4 h-4 mr-2"
                                />
                                Text
                              </button>
                            </div>
                          </div>
                        )}

                        {!isFirstSchedule && !isPast && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 rounded flex items-center justify-center">
                                <CalendarIcon />
                              </div>
                              <div className="text-xs text-base-coolGray">
                                SEND AGAIN
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="bg-white border rounded px-3 py-1 text-xs">
                                {scheduleDate.format('MMM DD, YYYY')}
                              </div>
                              <div className="flex gap-1">
                                <button className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px]">
                                  <img
                                    src={eyeIcon}
                                    alt="eye"
                                    className="w-4 h-4 mr-2"
                                  />
                                  Email
                                </button>
                                <button className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px]">
                                  <img
                                    src={eyeIcon}
                                    alt="eye"
                                    className="w-4 h-4 mr-2"
                                  />
                                  Text
                                </button>
                              </div>
                              <button className="w-6 h-6 flex items-center justify-center">
                                <RemoveIcon />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <span className="text-xs text-base-coolGray">
                    No follow-ups
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Right Column: Customers */}
          <div className="bg-white p-2 md:p-4 h-fit col-span-1 md:col-span-5 w-full rounded shadow-sm">
            <h5 className="font-bold text-sm mb-2">Customers</h5>
            <hr className="my-4" />
            {customers.length === 0 ? (
              <p className="text-base-coolGray text-sm">No customers</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[320px]">
                  <thead>
                    <tr>
                      <th className="text-left">Name</th>
                      <th className="text-left">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer: any) => (
                      <tr key={customer.id}>
                        <td>
                          {customer.firstName} {customer.lastName}
                        </td>
                        <td>{customer.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
